import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import ButtonItem from '@atlaskit/menu/button-item';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { fg } from '@confluence/feature-gating';

import { AgentPresenceAvatar } from './AgentPresenceAvatar.tsx';
import type { AgentWithPresence } from './AgentPresenceTypes.ts';

const i18n = defineMessages({
	editing: {
		id: 'team-presence.agent-group-menu.editing',
		defaultMessage: 'Editing',
		description:
			'The text is shown in the active AI agent menu item description when the AI agent is editing the page.',
	},
	editingWithActor: {
		id: 'team-presence.agent-group-menu.editing-with-actor',
		defaultMessage: 'with {actorName} • Editing',
		description:
			'The text is shown in the active AI agent menu item description when the AI agent is editing on behalf of a user.',
	},
	viewing: {
		id: 'team-presence.agent-group-menu.viewing',
		defaultMessage: 'Viewing',
		description:
			'The text is shown in the active AI agent menu item description when the AI agent is viewing the page.',
	},
	viewingWithActor: {
		id: 'team-presence.agent-group-menu.viewing-with-actor',
		defaultMessage: 'with {actorName} • Viewing',
		description:
			'The text is shown in the active AI agent menu item description when the AI agent is viewing on behalf of a user.',
	},
});

export type AgentPresenceMenuItemProps = {
	actingUserName?: string;
	agent: AgentWithPresence;
};

export const AgentPresenceMenuItem = ({
	actingUserName,
	agent,
}: AgentPresenceMenuItemProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleAgentClick = useCallback(
		(agentId: string) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'aiAgentPresenceGroupOverflowMenu',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'aiAgentPresenceGroupOverflowMenuItem',
					product: 'confluence',
					attributes: {
						agentId,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const getPresenceDescription = () => {
		if (!fg('platform_move_presence_agents')) {
			return undefined;
		}

		if (agent.presenceActivity === 'editor') {
			return actingUserName ? (
				<FormattedMessage {...i18n.editingWithActor} values={{ actorName: actingUserName }} />
			) : (
				<FormattedMessage {...i18n.editing} />
			);
		}

		return actingUserName ? (
			<FormattedMessage {...i18n.viewingWithActor} values={{ actorName: actingUserName }} />
		) : (
			<FormattedMessage {...i18n.viewing} />
		);
	};

	return (
		<ButtonItem
			onClick={() => handleAgentClick(agent.id)}
			iconBefore={
				fg('platform_move_presence_agents') ? (
					<AgentPresenceAvatar agent={agent} size="small" showBorder />
				) : (
					<AgentPresenceAvatar agent={agent} />
				)
			}
			description={getPresenceDescription()}
		>
			{agent.name}
		</ButtonItem>
	);
};
